/**
 * HDR の明るい画素を数段ぼかして足す。
 * トーンマップ前の lit にかけ、オーバーレイは対象外。
 */

import * as THREE from 'three';
import { FullScreenQuad } from 'three/addons/postprocessing/Pass.js';
import type { GraphicsQuality } from './graphics-quality';

const MAX_MIPS = 5;
const BLOOM_THRESHOLD = 1;
const BLOOM_KNEE = 0.35;
const BLOOM_WEIGHTS = [1, 0.7, 0.5, 0.35, 0.25];

const VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

const EXTRACT_FRAG = /* glsl */ `
uniform sampler2D tDiffuse;
uniform float uThreshold;
uniform float uKnee;
varying vec2 vUv;

void main() {
  vec3 color = texture2D(tDiffuse, vUv).rgb;
  float lum = dot(color, vec3(0.2126, 0.7152, 0.0722));
  float soft = clamp(lum - uThreshold + uKnee, 0.0, 2.0 * uKnee);
  soft = soft * soft / (4.0 * uKnee + 1e-4);
  float contrib = max(lum - uThreshold, soft) / max(lum, 1e-4);
  gl_FragColor = vec4(color * contrib, 1.0);
}
`;

const BLUR_FRAG = /* glsl */ `
uniform sampler2D tDiffuse;
uniform vec2 uDirection;
varying vec2 vUv;

void main() {
  vec3 color = texture2D(tDiffuse, vUv).rgb * 0.227027027;
  color += texture2D(tDiffuse, vUv + uDirection).rgb * 0.1945945946;
  color += texture2D(tDiffuse, vUv - uDirection).rgb * 0.1945945946;
  color += texture2D(tDiffuse, vUv + uDirection * 2.0).rgb * 0.1216216216;
  color += texture2D(tDiffuse, vUv - uDirection * 2.0).rgb * 0.1216216216;
  color += texture2D(tDiffuse, vUv + uDirection * 3.0).rgb * 0.0540540541;
  color += texture2D(tDiffuse, vUv - uDirection * 3.0).rgb * 0.0540540541;
  gl_FragColor = vec4(color, 1.0);
}
`;

const COMPOSITE_FRAG = /* glsl */ `
uniform sampler2D tDiffuse;
uniform sampler2D tBloom0;
uniform sampler2D tBloom1;
uniform sampler2D tBloom2;
uniform sampler2D tBloom3;
uniform sampler2D tBloom4;
uniform float uStrength;
uniform float uWeight0;
uniform float uWeight1;
uniform float uWeight2;
uniform float uWeight3;
uniform float uWeight4;
varying vec2 vUv;

void main() {
  vec3 color = texture2D(tDiffuse, vUv).rgb;
  vec3 bloom = texture2D(tBloom0, vUv).rgb * uWeight0;
  bloom += texture2D(tBloom1, vUv).rgb * uWeight1;
  bloom += texture2D(tBloom2, vUv).rgb * uWeight2;
  bloom += texture2D(tBloom3, vUv).rgb * uWeight3;
  bloom += texture2D(tBloom4, vUv).rgb * uWeight4;
  gl_FragColor = vec4(color + bloom * uStrength, 1.0);
}
`;

function makeTarget(width: number, height: number): THREE.WebGLRenderTarget {
  const target = new THREE.WebGLRenderTarget(Math.max(1, width), Math.max(1, height), {
    type: THREE.HalfFloatType,
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
    depthBuffer: false,
  });
  target.texture.generateMipmaps = false;
  target.texture.colorSpace = THREE.NoColorSpace;
  return target;
}

export class Bloom {
  private strength = 0.7;
  private mipCount = 4;
  private readonly size = new THREE.Vector2(1, 1);
  private readonly output: THREE.WebGLRenderTarget;
  private readonly extract: THREE.WebGLRenderTarget;
  private readonly mips: THREE.WebGLRenderTarget[] = [];
  private readonly blurTemps: THREE.WebGLRenderTarget[] = [];
  private readonly extractUniforms = {
    tDiffuse: { value: null as THREE.Texture | null },
    uThreshold: { value: BLOOM_THRESHOLD },
    uKnee: { value: BLOOM_KNEE },
  };
  private readonly blurUniforms = {
    tDiffuse: { value: null as THREE.Texture | null },
    uDirection: { value: new THREE.Vector2() },
  };
  private readonly compositeUniforms = {
    tDiffuse: { value: null as THREE.Texture | null },
    tBloom0: { value: null as THREE.Texture | null },
    tBloom1: { value: null as THREE.Texture | null },
    tBloom2: { value: null as THREE.Texture | null },
    tBloom3: { value: null as THREE.Texture | null },
    tBloom4: { value: null as THREE.Texture | null },
    uStrength: { value: 0.7 },
    uWeight0: { value: BLOOM_WEIGHTS[0] },
    uWeight1: { value: BLOOM_WEIGHTS[1] },
    uWeight2: { value: BLOOM_WEIGHTS[2] },
    uWeight3: { value: BLOOM_WEIGHTS[3] },
    uWeight4: { value: BLOOM_WEIGHTS[4] },
  };
  private readonly extractMaterial: THREE.ShaderMaterial;
  private readonly blurMaterial: THREE.ShaderMaterial;
  private readonly compositeMaterial: THREE.ShaderMaterial;
  private readonly quad: FullScreenQuad;

  constructor() {
    this.output = makeTarget(1, 1);
    this.extract = makeTarget(1, 1);
    for (let i = 0; i < MAX_MIPS; i++) {
      this.mips.push(makeTarget(1, 1));
      this.blurTemps.push(makeTarget(1, 1));
    }
    this.extractMaterial = new THREE.ShaderMaterial({
      uniforms: this.extractUniforms,
      vertexShader: VERT,
      fragmentShader: EXTRACT_FRAG,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
    });
    this.blurMaterial = new THREE.ShaderMaterial({
      uniforms: this.blurUniforms,
      vertexShader: VERT,
      fragmentShader: BLUR_FRAG,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
    });
    this.compositeMaterial = new THREE.ShaderMaterial({
      uniforms: this.compositeUniforms,
      vertexShader: VERT,
      fragmentShader: COMPOSITE_FRAG,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
    });
    this.quad = new FullScreenQuad(this.extractMaterial);
  }

  applyQuality(quality: GraphicsQuality): void {
    this.strength = quality.bloomStrength;
    this.mipCount = Math.max(1, Math.min(MAX_MIPS, Math.floor(quality.bloomMips)));
  }

  setSize(width: number, height: number): void {
    const w = Math.max(1, Math.floor(width));
    const h = Math.max(1, Math.floor(height));
    if (this.size.x === w && this.size.y === h) return;
    this.size.set(w, h);
    this.output.setSize(w, h);
    let mw = Math.max(1, Math.round(w / 2));
    let mh = Math.max(1, Math.round(h / 2));
    this.extract.setSize(mw, mh);
    for (let i = 0; i < MAX_MIPS; i++) {
      this.mips[i].setSize(mw, mh);
      this.blurTemps[i].setSize(mw, mh);
      mw = Math.max(1, Math.round(mw / 2));
      mh = Math.max(1, Math.round(mh / 2));
    }
  }

  /** lit HDR を受け取り、bloom を足したテクスチャを返す。 */
  apply(renderer: THREE.WebGLRenderer, input: THREE.Texture): THREE.Texture {
    const previousTarget = renderer.getRenderTarget();
    const previousAutoClear = renderer.autoClear;
    renderer.autoClear = true;

    this.extractUniforms.tDiffuse.value = input;
    this.quad.material = this.extractMaterial;
    renderer.setRenderTarget(this.extract);
    this.quad.render(renderer);
    this.extractUniforms.tDiffuse.value = null;

    let source = this.extract;
    for (let i = 0; i < this.mipCount; i++) {
      const mip = this.mips[i];
      const temp = this.blurTemps[i];
      const texelX = 1 / mip.width;
      const texelY = 1 / mip.height;

      this.blurUniforms.tDiffuse.value = source.texture;
      this.blurUniforms.uDirection.value.set(texelX, 0);
      this.quad.material = this.blurMaterial;
      renderer.setRenderTarget(temp);
      this.quad.render(renderer);

      this.blurUniforms.tDiffuse.value = temp.texture;
      this.blurUniforms.uDirection.value.set(0, texelY);
      renderer.setRenderTarget(mip);
      this.quad.render(renderer);
      this.blurUniforms.tDiffuse.value = null;
      source = mip;
    }

    this.compositeUniforms.tDiffuse.value = input;
    this.compositeUniforms.tBloom0.value = this.mips[0].texture;
    this.compositeUniforms.tBloom1.value = this.mips[1].texture;
    this.compositeUniforms.tBloom2.value = this.mips[2].texture;
    this.compositeUniforms.tBloom3.value = this.mips[3].texture;
    this.compositeUniforms.tBloom4.value = this.mips[4].texture;
    this.compositeUniforms.uStrength.value = this.strength;
    this.compositeUniforms.uWeight0.value = this.mipCount > 0 ? BLOOM_WEIGHTS[0] : 0;
    this.compositeUniforms.uWeight1.value = this.mipCount > 1 ? BLOOM_WEIGHTS[1] : 0;
    this.compositeUniforms.uWeight2.value = this.mipCount > 2 ? BLOOM_WEIGHTS[2] : 0;
    this.compositeUniforms.uWeight3.value = this.mipCount > 3 ? BLOOM_WEIGHTS[3] : 0;
    this.compositeUniforms.uWeight4.value = this.mipCount > 4 ? BLOOM_WEIGHTS[4] : 0;
    this.quad.material = this.compositeMaterial;
    renderer.setRenderTarget(this.output);
    this.quad.render(renderer);
    this.compositeUniforms.tDiffuse.value = null;
    this.compositeUniforms.tBloom0.value = null;
    this.compositeUniforms.tBloom1.value = null;
    this.compositeUniforms.tBloom2.value = null;
    this.compositeUniforms.tBloom3.value = null;
    this.compositeUniforms.tBloom4.value = null;

    renderer.setRenderTarget(previousTarget);
    renderer.autoClear = previousAutoClear;
    return this.output.texture;
  }

  dispose(): void {
    this.output.dispose();
    this.extract.dispose();
    for (const target of this.mips) target.dispose();
    for (const target of this.blurTemps) target.dispose();
    this.extractMaterial.dispose();
    this.blurMaterial.dispose();
    this.compositeMaterial.dispose();
    this.quad.dispose();
  }
}
